import {
  AfterContentChecked,
  Directive,
  ElementRef,
  HostListener, Input,
  NgZone,
  OnChanges,
  OnDestroy, OnInit,
} from '@angular/core';

@Directive({
  selector: '[autosize]'
})
export class AutosizeDirective implements OnInit, OnDestroy, OnChanges, AfterContentChecked {
  private windowResizeHandler;
  private textAreaElement: HTMLTextAreaElement;
  private oldContent: string;
  private oldWidth: number;
  private destroyed = false;
  private insidePdfPartOrCloneContainer: boolean;
  private maxHeight: number;

  @HostListener('input', ['$event.target'])
  onInput(textArea: HTMLTextAreaElement): void {
    this.adjust();
  }

  @Input() autosize = true;

  constructor(
    private element: ElementRef<HTMLTextAreaElement>,
    private zone: NgZone) {}

  ngOnInit() {
    if (!this.autosize) {
      return;
    }

    this.insidePdfPartOrCloneContainer = !!this.element.nativeElement.closest('.pdf-part-child') || !!this.element.nativeElement.closest('.drag-clone-container');

    this.textAreaElement = this.element.nativeElement;

    if (this.textAreaElement.style.maxHeight && this.textAreaElement.style.maxHeight.includes('px')) {
      this.maxHeight = +this.textAreaElement.style.maxHeight.replace('px', '');
    }

    if (!this.maxHeight) {
      this.textAreaElement.style['overflow-y'] = 'hidden';
    }

    if (this.insidePdfPartOrCloneContainer) {
      this.textAreaElement.style.height = '100%';
      return;
    }

    setTimeout(() => {
      this.adjust();
    });

    this.windowResizeHandler = Debounce(() => {
      this.zone.run(() => {
        this.adjust();
      });
    }, 200);

    this.zone.runOutsideAngular(() => {
      window.addEventListener('resize', this.windowResizeHandler, false);
    });
  }

  ngOnDestroy() {
    this.destroyed = true;
    if (this.windowResizeHandler) {
      window.removeEventListener('resize', this.windowResizeHandler, false);
    }
  }

  ngAfterContentChecked() {
    this.adjust();
  }

  ngOnChanges(changes) {
    this.adjust(true);
  }

  adjust(inputsChanged = false): void {
    if (this.insidePdfPartOrCloneContainer) {
      return;
    }

    if (!this.destroyed && this.textAreaElement && this.textAreaElement.parentNode) {
      const currentText = this.textAreaElement.value;

      if (inputsChanged === false &&
        currentText === this.oldContent &&
        this.textAreaElement.offsetWidth === this.oldWidth) {
        return;
      }

      this.oldContent = currentText;
      this.oldWidth = this.textAreaElement.offsetWidth;

      const clone = this.textAreaElement.cloneNode(true) as HTMLInputElement;
      const parent = this.textAreaElement.parentNode;
      clone.style.width = this.textAreaElement.offsetWidth + 'px';
      clone.style.visibility = 'hidden';
      clone.style.position = 'absolute';
      clone.textContent = currentText;

      parent.appendChild(clone);

      clone.style['overflow-y'] = 'hidden';
      clone.style.height = 'auto';

      let height = clone.scrollHeight;

      const computedStyle = window.getComputedStyle(clone, null);
      height += parseInt(computedStyle?.getPropertyValue('border-top-width'), 10);
      height += parseInt(computedStyle?.getPropertyValue('border-bottom-width'), 10);

      const oldHeight = this.textAreaElement.offsetHeight;

      if (!this.maxHeight) {
        this.textAreaElement.style['overflow-y'] = 'hidden';
      }

      if (!!this.maxHeight && height > this.maxHeight) {
        height = this.maxHeight;
      }

      (this.textAreaElement as HTMLTextAreaElement).style.height = height + 'px';

      parent.removeChild(clone);
    }
  }
}

function Debounce(func, wait, immediate = false) {
  let timeout;
  return () => {
    const context = this;
    const args = arguments;
    const later = () => {
      timeout = null;
      if (!immediate) {
        func.apply(context, args);
      }
    };
    const callNow = immediate && !timeout;
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
    if (callNow) {
      func.apply(context, args);
    }
  };
}
